// MegaMenu - full-width category menu under the header, driven by
// [data-mega-menu-trigger] buttons. Behavioral port of v1 modules/MegaMenu.js;
// the data-attribute contract (data-mega-menu, data-mega-menu-trigger,
// data-mega-menu-target, data-mega-menu-contents) and class names
// (is-active, is-mega-menu-active) are unchanged.
//
// The triggers also carry data-toggle="self" - the Toggle module flips their
// own is-active/aria-expanded state. MegaMenu must therefore init BEFORE
// Toggle (as in v1 main.js) so its click handler reads the pre-toggle state.
//
// v1 bug fixed: _closeMegaMenu (Escape / outside click) never reset
// isMegaMenuOpen, so the Tab focus trap stayed active over a closed menu.

import { doToggle, isToggled } from './toggle';
import { lockBody, unlockBody } from './modal';

export interface MegaMenuConfig {
  selector: string;
  selectorTriggers: string;
  selectorTargets: string;
  selectorContents: string;
}

const defaultConfig: Omit<MegaMenuConfig, 'selector'> = {
  selectorTriggers: '[data-mega-menu-trigger]',
  selectorTargets: '[data-mega-menu-target]',
  selectorContents: '[data-mega-menu-contents]',
};

const FOCUSABLE = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

export default class MegaMenu {
  config: MegaMenuConfig;

  megaMenu: Element | null = null;

  triggers: Element[] = [];

  targets: Element[] = [];

  isMegaMenuOpen = false;

  openMegaMenuSection: Element | null = null;

  constructor(selector = '[data-mega-menu]', config: Partial<MegaMenuConfig> = {}) {
    this.config = { selector, ...defaultConfig, ...config };

    this._init();
  }

  update = (): void => {
    this.destroy();
    this._init();
  };

  destroy = (): void => {
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this._handleTriggerClick);
    });
    document.removeEventListener('keydown', this._handleKeyDown);
    document.removeEventListener('click', this._handleClickOutside);
    this.triggers = [];
    this.targets = [];
  };

  private _init = (): void => {
    this.megaMenu = document.querySelector(this.config.selector);

    if (!this.megaMenu) {
      return;
    }

    this.triggers = [...document.querySelectorAll(this.config.selectorTriggers)];
    this.targets = [...document.querySelectorAll(this.config.selectorTargets)];

    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this._handleTriggerClick);
    });
    document.addEventListener('keydown', this._handleKeyDown);
  };

  private _handleTriggerClick = (e: Event): void => {
    const currentTrigger = (e.target as Element).closest(this.config.selectorTriggers);

    if (!currentTrigger || !this.megaMenu) {
      return;
    }

    // '[data-mega-menu-trigger]' -> 'data-mega-menu-trigger'
    const triggerAttribute = this.config.selectorTriggers.slice(1, -1);
    const targetSection = currentTrigger.getAttribute(triggerAttribute);

    this.isMegaMenuOpen = !isToggled(currentTrigger);
    this.openMegaMenuSection =
      (targetSection && document.querySelector(targetSection)) || null;

    if (this.isMegaMenuOpen) {
      lockBody();
      // Defer so the click that opened the menu doesn't immediately close it.
      requestAnimationFrame(() => {
        document.addEventListener('click', this._handleClickOutside);
      });
    } else {
      unlockBody();
      document.removeEventListener('click', this._handleClickOutside);
    }

    // Deactivate the other triggers - the current one toggles itself via its
    // own data-toggle="self" handled by the Toggle module.
    this.triggers.forEach(trigger => {
      if (trigger !== currentTrigger) {
        doToggle({
          target: trigger,
          className: 'is-active',
          icon: isToggled(trigger) ? trigger.getAttribute('data-toggle-icon') : null,
          expand: true,
          state: false,
        });
      }
    });

    // Show only the section the trigger points to.
    this.targets.forEach(target => {
      doToggle({
        target,
        className: 'is-active',
        state: target === this.openMegaMenuSection,
      });
    });

    doToggle({
      target: this.megaMenu,
      trigger: currentTrigger,
      className: 'is-active',
      state: this.isMegaMenuOpen,
    });

    doToggle({
      target: document.body,
      className: 'is-mega-menu-active',
      state: this.isMegaMenuOpen,
    });

    const contentsElement = document.querySelector(this.config.selectorContents);
    if (contentsElement) {
      doToggle({
        target: contentsElement,
        className: 'is-active',
        state: false,
      });
    }

    if (this.isMegaMenuOpen) {
      // Fix menu height for iOS manually (innerHeight - header height).
      const headerWrapper = document.querySelector<HTMLElement>('.header .header__wrapper');
      if (headerWrapper) {
        requestAnimationFrame(() => {
          const headerHeight = headerWrapper.offsetHeight;
          (this.megaMenu as HTMLElement).style.maxHeight =
            `${window.innerHeight - headerHeight}px`;
        });
      }

      // Focus first element upon opening.
      const focusableElement =
        this.openMegaMenuSection?.querySelector<HTMLElement>(FOCUSABLE);
      focusableElement?.focus();
    }
  };

  private _handleClickOutside = (e: Event): void => {
    const target = e.target as Element;

    if (
      this.megaMenu !== target &&
      !this.megaMenu?.contains(target) &&
      !target.closest(this.config.selectorTriggers)
    ) {
      this._closeMegaMenu();
      document.removeEventListener('click', this._handleClickOutside);
    }
  };

  private _handleKeyEscape = (): void => {
    const trigger = this.triggers.find(triggerElement => isToggled(triggerElement));

    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }

    this._closeMegaMenu();
  };

  private _closeMegaMenu = (): void => {
    if (this.megaMenu) {
      doToggle({
        target: this.megaMenu,
        className: 'is-active',
        state: false,
      });
    }

    doToggle({
      target: document.body,
      className: 'is-mega-menu-active',
      state: false,
    });

    this.triggers.forEach(trigger => {
      doToggle({
        target: trigger,
        className: 'is-active',
        icon: isToggled(trigger) ? trigger.getAttribute('data-toggle-icon') : null,
        expand: true,
        state: false,
      });
    });

    this.isMegaMenuOpen = false;
    this.openMegaMenuSection = null;

    unlockBody();
  };

  private _handleKeyDown = (e: KeyboardEvent): void => {
    const { key, shiftKey } = e;
    const { activeElement } = document;

    switch (key) {
      case 'Escape':
        this._handleKeyEscape();
        break;

      case 'ArrowDown':
        if (activeElement && this.triggers.includes(activeElement)) {
          e.preventDefault();
          (activeElement as HTMLElement).click();
        }
        break;

      case 'ArrowUp':
        if (this.isMegaMenuOpen) {
          this._handleKeyEscape();
        }
        break;

      case 'Tab':
        if (this.isMegaMenuOpen && this.openMegaMenuSection) {
          const focusableElements =
            this.openMegaMenuSection.querySelectorAll<HTMLElement>(FOCUSABLE);

          if (!focusableElements.length) {
            return;
          }

          const firstElement = focusableElements.item(0);
          const lastElement = focusableElements.item(focusableElements.length - 1);

          if (!shiftKey && activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          } else if (shiftKey && activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        }
        break;
    }
  };
}
